<script setup lang="ts">
import get from 'lodash/get'
import Dropdown from 'primevue/dropdown'

/**
 * @param {string} optionLabel
 * Only used if options is an array of objects like [{name: 'New York', code: 'NY'}] (for option name)
 * Example: optionLabel='name' or optionLabel='code'
 *
 * @param {string} optionValue
 * Only used if options is an array of objects like [{name: 'New York', code: 'NY'}] (for option value)
 * Example: optionValue='code'
 */

const props = defineProps<{
  modelValue: string
  name: string
  label?: string
  options: any[]
  optionLabel?: string
  optionValue?: string
  placeholder?: string
  disableField?: boolean
}>()

const emit = defineEmits<{ (e: 'update:modelValue', value: string): void }>()

const dropdown = ref<Dropdown>()
const focused = ref(false)

const onClick = () => {
  const isFocused = focused.value
  if (!isFocused) {
    dropdown.value?.show(true)
    focused.value = true
  }
}

const onBlur = () => {
  dropdown.value?.hide(false)
  focused.value = false
}

const isOptionSelected = (x: any) => {
  if (!props.optionValue && props.options.includes(x)) return true
  if (props.optionValue && get(props.options, props.optionValue) === x) return true
  return false
}

const onChange = (x: any) => {
  if (isOptionSelected(x)) {
    dropdown.value?.hide(true)
    focused.value = false
  }

  emit('update:modelValue', x)
}
</script>

<template>
  <McField :name="name" class="flex flex-col justify-end">
    <legend v-if="label" class="mb-3 text-2xs text-mcd-secondaryDarkGrey" :for="name">
      {{ label }}
    </legend>
    <Dropdown
      :id="name"
      ref="dropdown"
      class="h-full min-h-[1.625rem]"
      :class="{ '!h-fit': modelValue }"
      editable
      :model-value="modelValue"
      :options="options"
      :option-label="optionLabel"
      :option-value="optionValue"
      :placeholder="placeholder"
      :disabled="disableField"
      append-to="self"
      @click="onClick"
      @blur="onBlur"
      @hide="focused = false"
      @update:model-value="onChange"
    />
  </McField>
</template>

<style>
.p-dropdown-label {
  @apply p-0 text-base;
}

.p-dropdown {
  @apply rounded-none border-0 border-b-2 border-mcd-secondaryLightGrey shadow-none outline-0;
}

.p-dropdown > .p-dropdown-label,
.p-dropdown-panel {
  @apply text-mcd-black;
  font-family: 'Speedee', arial, helvetica, sans-serif !important;
}

.p-dropdown-item {
  @apply text-base;
}

.p-dropdown:enabled:focus {
  @apply border-0 border-b-2 border-mcd-secondaryLightGrey shadow-none;
}
.p-dropdown:not(:disabled).p-focus {
  @apply border-0 border-b-2 border-mcd-secondaryLightGrey shadow-none;
}

.p-dropdown:hover {
  @apply border-0 border-b-2 border-mcd-secondaryLightGrey shadow-none;
}
.p-dropdown:not(:disabled):hover {
  @apply border-0 border-b-2 border-mcd-secondaryLightGrey shadow-none;
}
</style>
